/**
 * GamelessGrid fills all available space with square video tiles for every
 * mate in the room (players and observers alike), sized as large as will fit.
 *
 * Why: "Gameless" mode hides the game board entirely so mates can use the
 * screen purely for video - no player/team grouping is shown, just everyone
 * present, each as large as the space allows.
 *
 * When to use: Rendered instead of the mate-list sidebar and game area while
 * the room's gameless flag is on.
 */
import * as React from 'react'
import { useSignals } from '@preact/signals-react/runtime'
import { MateCard } from '../../mateList/mateCard/MateCard/MateCard'
import { computeSquareGridLayout } from '../../../deps/ui/gamelessLayout/gamelessLayout'
import type { MateListVM } from '../../../deps/ui/mateListVM/mateListVM.types'
import { gamelessTestIds } from './gameless.testIds'

export interface GamelessGridProps {
  mateListVM: MateListVM
  /** Present only when the caller wants clicking our own mate's tile to open its settings. */
  onOurMateClick?: () => void
}

const GRID_GAP_PX = 16
const CONTAINER_PADDING_PX = 16
// CSS defines 1in as exactly 96px (independent of actual display DPI), so this
// keeps tiles from growing past ~4.8 physical inches (4in x 1.2) on a typical
// laptop screen even when there are only one or two mates in a large window.
const MAX_TILE_SIZE_PX = 4 * 96 * 1.2
const PIP_TILE_SIZE_PX = 128

export function GamelessGrid({ mateListVM, onOurMateClick }: GamelessGridProps) {
  useSignals()

  const [isSelfViewMinimized, setIsSelfViewMinimized] = React.useState(false)

  const players = mateListVM.players.value
  const observers = mateListVM.observers.value
  const ourMateId = mateListVM.ourMateId.value

  // Omni mates are the room owner's fake stand-ins for testing, not real people -
  // gameless mode is a video view, so it only ever shows real mates (see
  // wiki/concepts/vocabulary.md's "Gameless mode" entry).
  const allMates = [...players.flatMap((player) => player.mates), ...observers].filter((m) => !m.isOmniMate)
  const ourMate = allMates.find((m) => m.id === ourMateId)

  // If self view is minimized, exclude our mate from the main grid calculation so other tiles grow.
  const gridMates = isSelfViewMinimized && ourMate
    ? allMates.filter((m) => m.id !== ourMateId)
    : allMates

  const containerRef = React.useRef<HTMLDivElement | null>(null)
  const [containerSize, setContainerSize] = React.useState({ width: 0, height: 0 })

  // Measure available space so tiles can be sized as large as will fit - this
  // requires an actual DOM measurement, so it can't live in a pure dep.
  React.useEffect(() => {
    const el = containerRef.current
    if (!el) return

    function measure() {
      if (!el) return
      setContainerSize({ width: el.clientWidth, height: el.clientHeight })
    }

    measure()

    if (typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(measure)
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  const layout = computeSquareGridLayout(
    gridMates.length,
    containerSize.width - CONTAINER_PADDING_PX * 2,
    containerSize.height - CONTAINER_PADDING_PX * 2,
    GRID_GAP_PX,
    MAX_TILE_SIZE_PX
  )

  return (
    <div
      ref={containerRef}
      data-testid={gamelessTestIds.container}
      className="relative flex-1 min-w-0 min-h-0 overflow-hidden flex items-center justify-center p-4"
    >
      {gridMates.length === 0 ? (
        <div className="text-sm text-muted">
          {allMates.length === 0 ? 'No mates' : 'No other mates in call'}
        </div>
      ) : (
        <div
          className="grid"
          style={{
            gridTemplateColumns: `repeat(${layout.columns}, ${layout.tileSize}px)`,
            gap: `${GRID_GAP_PX}px`,
          }}
        >
          {gridMates.map((mate) => {
            const isOur = mate.id === ourMateId
            return (
              <MateCard
                key={mate.id}
                mate={mate}
                isOurMate={isOur}
                botDisplayName={mate.botDisplayName}
                sizePx={layout.tileSize}
                onClick={onOurMateClick}
                onToggleSelfViewMinimized={isOur ? () => setIsSelfViewMinimized(true) : undefined}
                isSelfViewMinimized={false}
              />
            )
          })}
        </div>
      )}

      {isSelfViewMinimized && ourMate && (
        <div
          data-testid={gamelessTestIds.selfViewPip}
          className="absolute bottom-4 right-4 z-30 flex flex-col items-end gap-1.5 p-1.5 bg-black/60 backdrop-blur-md rounded-lg border border-white/20 shadow-xl"
        >
          <div className="flex items-center justify-between w-full px-1 text-[11px] font-medium text-white/90">
            <span>You</span>
          </div>
          <MateCard
            mate={ourMate}
            isOurMate={true}
            botDisplayName={ourMate.botDisplayName}
            sizePx={PIP_TILE_SIZE_PX}
            onClick={onOurMateClick}
            onToggleSelfViewMinimized={() => setIsSelfViewMinimized(false)}
            isSelfViewMinimized={true}
          />
        </div>
      )}
    </div>
  )
}

